import { DEFAULT_PROPERTIES } from "./properties.js";
import { getDefaultPropertyValue, getPropertyByKeyPath } from "./properties.js";
import { expressions } from "../utils/expressions.js";
import { objects } from "../utils/objects.js";
import { types } from "../utils/types.js";
import { descriptors } from "./descriptors.js";
import { evaluateString, replaceRelationalKeyword, validateProperty } from "./evaluation.js";
import { store } from "./store.js";
import { createSingletonLogger } from "./singleton-logger.js";

class Configs {
  // Component identity for logging
  static componentName = "configs";
  
  #logger;
  
  constructor() {
    // Initialize the singleton logger
    this.#logger = createSingletonLogger(this);
  }
  _completeConfig(config) {
    const resultConfig = {};

    const defaultConfig = config.namespace
      ? objects.clone(descriptors.getDefaultConfig(config.namespace))
      : {};
    const snippets =
      config?.configSnippets?.map((snippetUid) => store.get(`snippets.${snippetUid}`)) || [];
    delete config["configSnippets"];

    const overlayConfigs = [defaultConfig, config, ...snippets];

    for (let overlayConfig of overlayConfigs) {
      this._overlayConfigs(resultConfig, overlayConfig);
    }

    return resultConfig;
  }

  resolveConfig(config, event) {
    config = this._completeConfig(config, event);

    return this._resolveObject(config, event);
  }

  // eslint-disable-next-line max-params
  _resolveProperty(config, event, property, propertyDescriptor, options) {
    if (
      typeof property !== "string" ||
      propertyDescriptor?.type === "expression" ||
      expressions.isNonComputingValue(property)
    ) {
      // String value for a non-string/non-expression type with no core tokens
      // is an unresolved expression (e.g. "50%" for an int) — return as-is
      if (
        typeof property === "string" &&
        propertyDescriptor?.type !== "string" &&
        propertyDescriptor?.type !== "expression" &&
        expressions.isNonComputingValue(property)
      ) {
        return property;
      }

      //in this condition property could only be numeric, string or boolean
      const context = config?.uid && config?.namespace
        ? { uid: config.uid, namespace: config.namespace }
        : {};
      const { isValid } = validateProperty(
        property,
        propertyDescriptor,
        options?.isArrayItem,
        context,
      );
      const defaultValue = propertyDescriptor
        ? getDefaultPropertyValue(propertyDescriptor)
        : property;
      return isValid ? property : defaultValue;
    }

    const propertyWithCorrectPropertyUid = replaceRelationalKeyword(config, property);

    const contextOptions = {
      ...options,
      context: config?.uid && config?.namespace 
        ? { uid: config.uid, namespace: config.namespace }
        : {}
    };
    return evaluateString(
      config,
      event,
      propertyWithCorrectPropertyUid,
      propertyDescriptor,
      contextOptions,
    );
  }

  _resolveArray(config, event, array, propertyDescriptor) {
    if (!expressions.isPrimitiveType(propertyDescriptor.valueType)) {
      if (propertyDescriptor.valueType && descriptors.get(propertyDescriptor.valueType)) {
        for (const [index, item] of array.entries()) {
          if (types.isObject(item)) {
            array[index] = this.resolveConfig({ ...item, namespace: propertyDescriptor.valueType }, event);
          }
        }
      }
      return array;
    }
    for (const [index, valueItem] of array.entries()) {
      array[index] = this._resolveProperty(config, event, valueItem, propertyDescriptor, {
        isArrayItem: true,
      });
    }
    return array;
  }

  _flattenGroups(config, descriptor) {
    for (const key of Object.keys(config)) {
      if (descriptor.properties[key]?.type !== "group") {
        continue;
      }

      const value = config[key];

      if (!types.isObject(value) || types.isArray(value)) {
        continue;
      }

      if (value.isCoreData || value.namespace) {
        continue;
      }

      this._flattenGroupObject(config, key, value, descriptor);
      delete config[key];
    }
  }

  _flattenGroupObject(config, groupName, groupObject, descriptor) {
    const isOptionalGroup = descriptor.properties[groupName].optional;

    if (isOptionalGroup) {
      for (const propertyDescriptor of Object.values(descriptor.properties)) {
        if (propertyDescriptor.group !== groupName) {
          continue;
        }
        if (propertyDescriptor.optional) {
          continue;
        }
        if (!(propertyDescriptor.name in config)) {
          config[propertyDescriptor.name] = getDefaultPropertyValue(propertyDescriptor);
        }
      }
    }

    for (const [key, value] of Object.entries(groupObject)) {
      const flatKey = `${groupName}.${key}`;

      if (types.isObject(value) && "value" in value) {
        config[flatKey] = value.value;
        for (const [subKey, subValue] of Object.entries(value)) {
          if (subKey === "value") {
            continue;
          }
          config[`${flatKey}.${subKey}`] = subValue;
        }
      } else {
        config[flatKey] = value;
      }
    }
  }

  _resolveObject(config, event) {
    const descriptor =
      config.namespace &&
      (descriptors.get(config.namespace) ?? descriptors.getMethodDescriptor(config.namespace));

    if (!descriptor) {
      return config;
    }

    this._flattenGroups(config, descriptor);

    for (const property of Object.entries(config)) {
      const [key, value] = property;
      const propertyDescriptor = descriptor?.properties[key];

      const isPropertyOutOfDescriptor = descriptor && !propertyDescriptor;
      const isDefaultProperty = DEFAULT_PROPERTIES.includes(key);
      const isDynamicGroupProperty = this._isDynamicGroupProperty(key, descriptor);
      if (isPropertyOutOfDescriptor && !isDefaultProperty && !isDynamicGroupProperty) {
        delete config[key];
        continue;
      }

      if (
        !types.isObject(value) &&
        expressions.isEvent(value) &&
        !expressions.isExpression(value)
      ) {
        config[key] = this._replaceEventPropertyWithValue(value, event, propertyDescriptor);
      }

      if (!types.isObject(value) && !types.isArray(value)) {
        config[key] = this._resolveProperty(config, event, value, propertyDescriptor);
        continue;
      }

      if (types.isArray(value)) {
        config[key] = this._resolveArray(config, event, value, propertyDescriptor);
        continue;
      }

      if (types.isObject(value)) {
        if (propertyDescriptor && !expressions.isPrimitiveType(propertyDescriptor.type) && descriptors.get(propertyDescriptor.type)) {
          config[key] = this.resolveConfig({ ...value, namespace: propertyDescriptor.type }, event);
        } else {
          const completedValue = this._completeConfig(value);
          if (completedValue.isCoreData) {
            config[key] = this._resolveObject(completedValue, event);
          }
        }
      }
    }
    return config;
  }

  _replaceEventPropertyWithValue(propertyValue, message, propertyDescriptor) {
    if (propertyDescriptor?.type === "expression") {
      return propertyValue;
    }

    if (propertyDescriptor?.type === "string") {
      let result = propertyValue;
      const propertyPaths = expressions.extractVariablesFromExpression(
        propertyValue,
        expressions.getEventSpecialChar(),
      );

      for (const propertyPath of propertyPaths) {
        const [_, ...keys] = propertyPath.split(".");
        const mappedKeys = this._mapEventKeysToMessage(keys);
        result = result.replace("@{" + propertyPath + "}", getPropertyByKeyPath(mappedKeys, message));
      }
      return result;
    }

    const propertyPath = expressions.removeBrackets(propertyValue);
    const [_, ...keys] = propertyPath.split(".");
    const mappedKeys = this._mapEventKeysToMessage(keys);
    return getPropertyByKeyPath(mappedKeys, message);
  }

  _mapEventKeysToMessage(keys) {
    if (keys.length === 0) return keys;
    const mapped = [...keys];
    if (mapped[0] === "data") mapped[0] = "value";
    else if (mapped[0] === "name") mapped[0] = "key";
    return mapped;
  }

  _isDynamicGroupProperty(key, descriptor) {
    const dotIndex = key.indexOf(".");
    if (dotIndex === -1) return false;
    const groupName = key.substring(0, dotIndex);
    const groupDescriptor = descriptor.properties[groupName];
    return groupDescriptor?.type === "group" && groupDescriptor?.dynamic;
  }

  _overlayConfigs(baseConfig, configToOverlay) {
    for (let key in configToOverlay) {
      baseConfig[key] = configToOverlay[key];
    }

    return baseConfig;
  }
}

const configs = new Configs();
export { Configs, configs };